import { Alert, AlertDescription, AlertTitle } from "design-system/atoms/Alert"
import { Button } from "design-system/atoms/Button"
import { Input } from "design-system/atoms/Input"
import { SheetDescription, SheetHeader, SheetTitle } from "design-system/atoms/Sheet"
import { Textarea } from "design-system/atoms/Textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { MarkdownRenderer } from "design-system/organisms/chat/MarkdownRenderer"
import { FormControl, FormDescription, FormField, FormItem } from "design-system/organisms/Form"
import { ensure } from "lopermut"
import { FunctionSquare, Trash } from "lucide-react"
import { type JSX, useMemo } from "react"
import { type FieldPath, useFieldArray, UseFormReturn, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import {
  WorkflowConditionDefinition,
  WorkflowConditionFieldEnum,
  WorkflowConditionLogicOperatorEnum,
  WorkflowEventEnum,
  conditionValueKind,
} from "schema"
import { useWorkflowBuilder } from "../hooks/useWorkflowBuilder"
import { useEventPayload } from "../hooks/useEventPayload"
import { BuilderFormValues } from "../lib/workflowsCanvas"

type ConditionRuleItemProps = {
  form: UseFormReturn<BuilderFormValues>
  nodeIndex: number
  itemIndex: number
  triggerEventType: string | null | undefined
  onRemove: (index: number) => void
}

function ConditionRuleItem({
  form,
  nodeIndex,
  itemIndex,
  triggerEventType,
  onRemove,
}: ConditionRuleItemProps) {
  const { t } = useTranslation()
  const conditionPath = `nodes.${nodeIndex}.conditions.${itemIndex}` as const

  // NOTE: Types are wrong here, as it can be undefined too
  const _fieldItem = form.watch(conditionPath)
  const fieldItem = _fieldItem as typeof _fieldItem | undefined
  const isLlmField = fieldItem?.field === WorkflowConditionFieldEnum.LLMPrompt

  function renderValueInput() {
    if (!fieldItem) return null

    const valueKind = conditionValueKind(fieldItem.field, fieldItem.operator)

    switch (valueKind) {
      case "none":
        return null

      case "select":
        const isTranslatable =
          "isTranslatable" in workflowConditionDefinition &&
          workflowConditionDefinition.isTranslatable
        const entries =
          "values" in workflowConditionDefinition
            ? Object.entries(workflowConditionDefinition.values)
            : []

        return (
          <FormField
            control={form.control}
            name={`${conditionPath}.value`}
            render={({ field: valueField }) =>
              (() => {
                let selectValueLabel: string | JSX.Element | undefined

                if (!valueField.value || !("values" in workflowConditionDefinition)) {
                  selectValueLabel = (
                    <span className="text-muted-foreground">
                      {t("campaigns.condition.valueSelectPlaceholder")}
                    </span>
                  )
                } else if (workflowConditionDefinition.isTranslatable) {
                  selectValueLabel = t(
                    `campaigns.condition.values.${fieldItem.field}.${valueField.value}`,
                  )
                } else {
                  selectValueLabel =
                    workflowConditionDefinition.values[
                      valueField.value as keyof typeof workflowConditionDefinition.values
                    ]
                }

                return (
                  <FormItem>
                    <FormControl>
                      <Select
                        value={valueField.value}
                        onValueChange={(newValue) => {
                          valueField.onChange(newValue, { shouldDirty: true })
                          form.trigger(`${conditionPath}.value`)
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue>{selectValueLabel}</SelectValue>
                        </SelectTrigger>
                        <SelectContent className="max-h-96 overflow-y-auto">
                          {entries.map(([key, val]) => {
                            let itemLabel = val
                            if (isTranslatable) {
                              itemLabel = t(`campaigns.condition.values.${fieldItem.field}.${key}`)
                            }

                            return (
                              <SelectItem key={key} value={key}>
                                {itemLabel}
                              </SelectItem>
                            )
                          })}
                        </SelectContent>
                      </Select>
                    </FormControl>
                  </FormItem>
                )
              })()
            }
          />
        )

      case "input":
        return (
          <FormField
            control={form.control}
            name={`${conditionPath}.value`}
            render={({ field: valueField }) => (
              <FormItem>
                <FormControl>
                  <Input
                    value={valueField.value}
                    onChange={(e) => {
                      valueField.onChange(e.target.value, { shouldDirty: true })
                      form.trigger(`${conditionPath}.value`)
                    }}
                    placeholder={t("campaigns.condition.valueInputPlaceholder")}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        )
    }
  }

  function renderPromptInput() {
    if (!isLlmField) return null

    return (
      <FormField
        control={form.control}
        name={`${conditionPath}.config.prompt`}
        render={({ field }) => (
          <FormItem className="flex flex-col gap-2">
            <FormControl>
              <Textarea
                value={field.value ?? ""}
                onChange={(e) => {
                  field.onChange(e.target.value, { shouldDirty: true })
                  form.trigger(`${conditionPath}.config.prompt`)
                }}
                placeholder={t("campaigns.condition.llm.promptPlaceholder")}
                rows={6}
              />
            </FormControl>
            <FormDescription>{t("campaigns.condition.llm.promptDescription")}</FormDescription>
          </FormItem>
        )}
      />
    )
  }

  function renderOperatorInput() {
    return (
      <FormField
        control={form.control}
        name={`${conditionPath}.operator`}
        render={({ field }) => (
          <FormItem className="flex flex-col gap-2">
            <FormControl>
              <Select
                value={field.value}
                onValueChange={(newValue) => {
                  field.onChange(newValue, { shouldDirty: true })
                  if (!isLlmField) {
                    form.setValue(`${conditionPath}.value`, null, { shouldDirty: true })
                  }
                  form.trigger(`${conditionPath}.operator`)
                }}
                disabled={!operatorEnabled}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("campaigns.condition.operatorPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {operatorEnabled
                    ? Object.values(workflowConditionDefinition.operators).map((op) => (
                        <SelectItem key={op} value={op}>
                          {t(`campaigns.condition.operators.${op}`)}
                        </SelectItem>
                      ))
                    : null}
                </SelectContent>
              </Select>
            </FormControl>
          </FormItem>
        )}
      />
    )
  }

  if (!fieldItem) return null

  const workflowConditionDefinition = WorkflowConditionDefinition[fieldItem.field]
  const operatorEnabled = !!fieldItem?.field

  const availableFields = Object.values(WorkflowConditionFieldEnum).filter((val) => {
    if (val === WorkflowConditionFieldEnum.CapabilityExecutionStatus) {
      return triggerEventType === WorkflowEventEnum.CapabilityExecuted
    }
    return true
  })

  return (
    <div className="flex flex-col gap-1">
      {itemIndex !== 0 && (
        <div className="flex items-center justify-between">
          <FormField
            control={form.control}
            name={`${conditionPath}.logic`}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Select
                    value={field.value ?? undefined}
                    onValueChange={(newValue) => {
                      field.onChange(newValue, { shouldDirty: true })
                    }}
                  >
                    <SelectTrigger className="w-20 p-2 h-9">
                      <SelectValue placeholder={t("campaigns.condition.logicPlaceholder")} />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(WorkflowConditionLogicOperatorEnum).map((enumValue) => (
                        <SelectItem key={enumValue} value={enumValue}>
                          {t(`campaigns.condition.logics.${enumValue}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
              </FormItem>
            )}
          />
          <Button variant="ghost" size="icon" onClick={() => onRemove(itemIndex)}>
            <Trash className="w-4 h-4" />
          </Button>
        </div>
      )}
      <FormField
        control={form.control}
        name={`${conditionPath}.field`}
        render={({ field }) => (
          <FormItem className="flex flex-col gap-2">
            <FormControl>
              <Select
                value={field.value}
                onValueChange={(newValue) => {
                  field.onChange(newValue, { shouldDirty: true })
                  // Dynamic path — RHF can't verify template literal paths at compile time
                  form.setValue(
                    `${conditionPath}.operator` as FieldPath<BuilderFormValues>,
                    undefined,
                    { shouldDirty: true },
                  )
                  form.setValue(`${conditionPath}.value`, null, { shouldDirty: true })
                  form.setValue(
                    `${conditionPath}.config`,
                    newValue === WorkflowConditionFieldEnum.LLMPrompt ? { prompt: "" } : {},
                    { shouldDirty: true },
                  )
                  form.trigger(`${conditionPath}.field`)
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("campaigns.condition.fieldPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {availableFields.map((val) => (
                    <SelectItem key={val} value={val}>
                      {t(`campaigns.condition.fields.${val}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormControl>
          </FormItem>
        )}
      />
      {isLlmField ? (
        <>
          {renderOperatorInput()}
          {renderPromptInput()}
        </>
      ) : (
        <>
          {renderOperatorInput()}
          {renderValueInput()}
        </>
      )}
    </div>
  )
}

export function ConditionSheet() {
  const { t } = useTranslation()
  const { form, focusedNode } = useWorkflowBuilder()

  const nodes = useWatch({ control: form.control, name: "nodes" })

  const nodeIndex = useMemo(
    () => ensure(nodes?.findIndex((node) => node.id === focusedNode?.id)),
    [nodes, focusedNode],
  )

  const { eventNode, eventPayload, eventName } = useEventPayload({ nodeId: focusedNode?.id ?? "" })
  const triggerEventType = eventNode?.event ?? null

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: `nodes.${nodeIndex}.conditions` as const,
  })

  const onAddCondition = () => {
    append({
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      field: undefined,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      operator: undefined,
      value: undefined,
      logic: WorkflowConditionLogicOperatorEnum.And,
    })
  }

  const conditions = form.watch(`nodes.${nodeIndex}.conditions`)
  const showAndOrWarning =
    conditions &&
    conditions.length > 1 &&
    conditions.some((condition) => condition.logic === "Or") &&
    conditions.some((condition) => condition.logic === "And")
  const hasLlmCondition = conditions?.some(
    (condition) => condition.field === WorkflowConditionFieldEnum.LLMPrompt,
  )

  const isInvalidNode = nodeIndex < 0

  if (isInvalidNode) return null

  return (
    <SheetHeader className="flex flex-col gap-4 text-left">
      <div className="flex flex-col gap-2">
        <SheetTitle>{t("campaigns.condition.label")}</SheetTitle>
        <SheetDescription>{t("campaigns.condition.description")}</SheetDescription>
      </div>
      {showAndOrWarning && (
        <Alert variant="warning">
          <FunctionSquare className="w-5 h-5" />
          <AlertTitle>{t("campaigns.condition.andOrCombination.title")}</AlertTitle>
          <AlertDescription>
            {t("campaigns.condition.andOrCombination.description")}
          </AlertDescription>
        </Alert>
      )}
      <div className="flex flex-col gap-4">
        {fields.map((fieldItem, itemIndex) => (
          <ConditionRuleItem
            key={fieldItem.id}
            form={form}
            nodeIndex={nodeIndex}
            itemIndex={itemIndex}
            triggerEventType={triggerEventType}
            onRemove={remove}
          />
        ))}
        <Button variant="outline" onClick={onAddCondition} className="w-fit">
          {t("campaigns.condition.addCondition")}
        </Button>
        {hasLlmCondition &&
          (eventName && eventPayload ? (
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium">
                {t("campaigns.condition.llm.payloadTitle")}
              </span>
              <span className="text-sm text-muted-foreground">
                {t("campaigns.condition.llm.payloadDescription", { eventName })}
              </span>
              <div className="rounded-md border border-border bg-muted/30 p-3 overflow-auto max-h-80">
                <MarkdownRenderer>{`\`\`\`json\n${JSON.stringify(
                  eventPayload,
                  null,
                  2,
                )}\n\`\`\``}</MarkdownRenderer>
              </div>
            </div>
          ) : (
            <span className="text-sm text-muted-foreground">
              {t("campaigns.condition.llm.noEventSelected")}
            </span>
          ))}
      </div>
    </SheetHeader>
  )
}
